/**
 * runMonthlyCreditAmortization — Amortissement mensuel des crédits.
 *
 * 11/02/2026 (Phase 5 — Dette technique).
 * Extrait du bloc inline de `coreSlice.nextDay` (lignes 1442-1494 de la
 * version 1017 lignes de nextDay). Reduit `nextDay` de ~55 lignes.
 *
 * Comportement :
 *   Pour chaque crédit actif, calcule la ligne d'amortissement du mois
 *   (interest = principalRestant × annualRate / 12, principal_portion =
 *   monthlyPayment − interest, nouveau principal, nouvelle durée).
 *   Rebuild immutable du tableau `credits`. Les crédits totalement
 *   remboursés sont retirés et leur `bankId` / `mortgageVehicleId` sont
 *   collectés pour être traités en aval (scoring bancaire + libération
 *   des gages hypothécaires).
 *
 * @param {Array} credits — state.credits actuel (peut être vide)
 * @returns {{
 *   updatedCredits: Array,
 *   repaidBankIds: string[],
 *   repaidMortgageVehicleIds: Set<string>,
 * }}
 *
 * Fonction PURE : aucun accès à get()/set(). Idempotent : appeler avec
 * un tableau vide renvoie un tableau vide.
 */
export function runMonthlyCreditAmortization(credits = []) {
  const newCredits = [];
  const repaidBankIds = [];
  const repaidMortgageVehicleIds = new Set();

  for (const c of credits) {
    if ((c.remainingMonths || 0) <= 0) {
      // Déjà terminé lors d'un tick précédent (défensif — ne devrait pas
      // arriver avec le cleanup actuel, mais inoffensif si ça arrive).
      continue;
    }
    // Amortissement standard (loan amortizing schedule) :
    //   interest = remainingPrincipal × annualRate / 12
    //   principal_portion = monthlyPayment − interest
    //   new_remainingPrincipal = remainingPrincipal − principal_portion
    const interest = (c.remainingPrincipal || 0) * (c.annualRate || 0) / 12;
    const principalPortion = (c.monthlyPayment || 0) - interest;
    const nextRemainingPrincipal = Math.max(0, (c.remainingPrincipal || 0) - principalPortion);
    const nextRemainingMonths = (c.remainingMonths || 0) - 1;
    if (nextRemainingMonths <= 0) {
      // Prêt entièrement remboursé — collecter pour scoring + libération.
      if (c.bankId) repaidBankIds.push(c.bankId);
      if (c.mortgageVehicleId) repaidMortgageVehicleIds.add(c.mortgageVehicleId);
    } else {
      newCredits.push({
        ...c,
        remainingMonths: nextRemainingMonths,
        remainingPrincipal: nextRemainingPrincipal,
        // 18/06/2026 — mensualité offerte constructeur : l'amortissement
        // a lieu normalement (le constructeur la paye), on décrémente.
        ...((c.freeMonthsRemaining || 0) > 0
          ? { freeMonthsRemaining: c.freeMonthsRemaining - 1 }
          : {}),
      });
    }
  }

  return {
    updatedCredits: newCredits,
    repaidBankIds,
    repaidMortgageVehicleIds,
  };
}
